/**
 * Your card: what is on it, when it comes off, and what it costs while it is
 * there.
 *
 * The write-up is the game's only real punishment, and everything about this
 * module is about making it feel like a hotel rather than a health bar:
 *
 *   - Reasons are *named*. "Front of house in uniform" is a different thing
 *     from "missed a deadline", and the file reads back as a file.
 *   - Each reason has its own cooldown. Without one, standing in the lobby for
 *     four seconds is a full run over, which is not a rule, it is a trapdoor.
 *     With one, the lobby is somewhere you can be for a moment and get away
 *     with, which is exactly what makes cutting through it tempting.
 *   - Nothing on it is permanent. Every entry carries the day it clears, fixed
 *     the moment it is filed, and a card that is clear on Friday was a bad week
 *     rather than a dead run.
 *   - The third one costs you shifts, not the day. What that means is the
 *     caller's business — see below.
 *
 * It does not end anything itself. It says the count reached the limit and
 * lets whoever owns the save decide what that means, for the same reason the
 * shift tracker does not issue its own write-up.
 *
 * ---------------------------------------------------------------------------
 * THE THREE NUMBERS, AND WHY THEY ARE THESE ONES
 *
 * `DECAY_SHIFTS`      five. A working week. Long enough that two on a Monday
 *                     are both still there on the Thursday you were hoping to
 *                     have forgotten them, short enough that a player who works
 *                     clean can watch the card empty rather than reading about
 *                     it. Fixed at filing time and never pushed back by a later
 *                     write-up: the clearing day is *shown* on the card, and a
 *                     date that quietly moves is worse than no date at all.
 *
 * `RECORD_PENALTY`    what each strike *after the first* takes off how a
 *                     department reads you, before the roster weighs it. The
 *                     first one is free of this, which is the whole shape of
 *                     the thing: one is a bad afternoon and two is being in
 *                     trouble, and the building can tell the difference without
 *                     anybody announcing it.
 *
 *                     Free of *this*, not free. A write-up costs standing the
 *                     moment it is filed, every time, and that is what a bad
 *                     afternoon costs. What the second one adds is that people
 *                     start reading you differently — and it is sized against
 *                     the ladder in `standing.js` to take a new hire off the
 *                     step they start on in one go, with room to spare, so that
 *                     "in trouble" is a state and not a rounding error.
 *
 * `SUSPENSION_SHIFTS` two, then four, then you are finished. See the note on
 *                     `nextSuspension` for why it terminates and why it takes
 *                     nine write-ups and three ignored warnings to get there.
 * ---------------------------------------------------------------------------
 */

export const STRIKES = 3;

/** Shifts a write-up stays on your card before it ages off. */
export const DECAY_SHIFTS = 5;

/**
 * What each live strike past the first takes off how a department reads you, at
 * weight 1.
 *
 * A standing *penalty*, not a standing change: the number in the ledger is
 * untouched and what moves is what people see when they look at you. Which
 * means it lifts the moment the card clears, without anything having to
 * remember to give it back — and a player who works a clean week gets their
 * doors back rather than a permanent dent they can never account for.
 *
 * How heavily any one person weighs it is on their row in `staff.js`, because
 * "what does this character make of somebody in trouble" is a fact about the
 * character. Nothing in here knows a name.
 */
export const RECORD_PENALTY = 28;

/** Shifts lost to a first suspension, then a second. The third is the end. */
export const SUSPENSION_SHIFTS = Object.freeze([2, 4]);

/** What a write-up costs you, before the rivalry matrix gets hold of it. */
export const WRITEUP_COST = Object.freeze({ management: -6, banquets: -4 });

export const REASONS = Object.freeze({
  FOH: 'front-of-house',
  LATE: 'late',
  NO_SHOW: 'no-show',
  DEADLINE: 'missed-deadline',
  RESTRICTED: 'restricted-area',
  /**
   * Back of house in your own clothes, on the clock.
   *
   * The mirror of FOH, and a separate line on the form rather than the same one
   * with a different detail, because they are opposite mistakes. One is staff
   * where guests are; this is a stranger where staff are. A file that called
   * both of them "front of house" would read as a manager who cannot tell the
   * two apart, and the player would learn one rule instead of two.
   */
  CIVILIAN: 'out-of-uniform',
  REPORTED: 'reported',
  THEFT: 'theft',
});

/** Seconds of real time before the same offence can be written up again. */
const COOLDOWN = Object.freeze({
  [REASONS.FOH]: 25,
  [REASONS.RESTRICTED]: 25,
  // Same as the other two states you can drift into, for the same reason: a
  // room you cross in four seconds must not be a trapdoor.
  [REASONS.CIVILIAN]: 25,
  [REASONS.LATE]: 0,
  [REASONS.NO_SHOW]: 0,
  [REASONS.DEADLINE]: 0,
  // Both are deliberate acts, not states you can drift into, so there is
  // nothing to protect the player from repeating.
  [REASONS.REPORTED]: 0,
  [REASONS.THEFT]: 0,
});

/**
 * How heavily one person reads your card.
 *
 * Read off the roster row, the same way every other disposition is, so the file
 * that holds the seven is still the only place any of it is written down. A row
 * with no `record` block weighs it exactly once, which is the ordinary reaction
 * of somebody who has heard.
 */
export function weighsRecord(person) {
  return person?.record?.weighs ?? 1;
}

/**
 * What `strikes` live write-ups take off how this person reads your standing.
 *
 * A number of points, always positive, to be subtracted from the ledger by
 * whoever is doing the reading. It is never applied *to* the ledger: standing
 * is what you earned and this is what your card is doing to it this week.
 *
 * The first strike is not counted, and that is a rule rather than a taper. A
 * write-up already costs you standing on the way in; if it also moved how
 * everybody reads you, then one bad afternoon — clocking in six minutes late —
 * would shut three doors across the property, and the shutting would be
 * invisible because nobody announces a rung. Two is when the building starts
 * treating you differently, which is also the count your manager turns up
 * about, and those being the same number is the point.
 */
export function penaltyFor(person, strikes) {
  return Math.max(0, strikes - 1) * RECORD_PENALTY * weighsRecord(person);
}

/** The day a write-up filed on `day` comes off the card. */
export function clearsOn(day, decayShifts = DECAY_SHIFTS) {
  return (day ?? 0) + decayShifts;
}

export function createWriteups({ max = STRIKES, decayShifts = DECAY_SHIFTS } = {}) {
  let entries = [];
  /** Suspensions already served. The only thing that survives a card clearing. */
  let served = 0;
  const cooldowns = new Map();
  const listeners = new Set();

  return {
    max,
    decayShifts,
    get count() {
      return entries.length;
    },
    get entries() {
      return [...entries];
    },
    get remaining() {
      return Math.max(0, max - entries.length);
    },
    /** How many suspensions have been served. Not cleared by a clean week. */
    get served() {
      return served;
    },

    onEvent(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Whether this offence would land right now, or is still cooling off. */
    armed(reason, now = performance.now()) {
      const until = cooldowns.get(reason) ?? 0;
      return now >= until;
    },

    /**
     * Files one. Returns the entry, or null if the offence is on cooldown.
     *
     * `day` and `at` are recorded so the file can be read back as a history
     * rather than a count, which is what the save wants and what a manager
     * would actually be holding. `clears` is recorded with them, once, rather
     * than computed on the way out: it is printed on the card, the player is
     * expected to plan around it, and a date that is recalculated every time it
     * is read is a date that can quietly stop being the one they read.
     */
    add(reason, { detail = '', day = null, at = null, now = performance.now() } = {}) {
      if (!this.armed(reason, now)) return null;
      cooldowns.set(reason, now + (COOLDOWN[reason] ?? 20) * 1000);

      const entry = {
        reason,
        detail,
        day,
        at,
        clears: clearsOn(day, decayShifts),
        index: entries.length + 1,
      };
      entries.push(entry);
      const over = entries.length >= max;
      for (const listener of listeners) listener({ entry, count: entries.length, over });
      return entry;
    },

    /**
     * Takes off everything that has aged out. Returns what came off.
     *
     * Called by whatever moves the calendar, which is the only thing that can
     * know a day has passed. Entries with no day on them — a script filing one
     * straight into the module — never age, because a write-up with no date is
     * not a thing a manager could have filed and there is nothing to count
     * from.
     */
    expire(day) {
      if (!entries.length) return [];
      const gone = entries.filter((entry) => entry.day !== null && day >= entry.clears);
      if (!gone.length) return [];
      entries = entries
        .filter((entry) => !gone.includes(entry))
        // The card is numbered by what is on it. Leaving the old indices would
        // show a player "2 of 3" on a card with one line.
        .map((entry, index) => ({ ...entry, index: index + 1 }));
      for (const listener of listeners) listener({ expired: gone, count: entries.length, over: false });
      return gone;
    },

    /**
     * What the next time you reach the limit costs, without spending it.
     *
     * Two shifts, then four, then there is nothing left to take and you are
     * finished. It terminates on purpose and the path is long on purpose: nine
     * write-ups, and — because the second strike of every set is the one the
     * supervisor turns up about — three explicit warnings, said to your face,
     * with the clearing dates on them. Nobody arrives at the end of this
     * without having been told three times what was at the end of it.
     */
    nextSuspension() {
      const number = served + 1;
      if (number > SUSPENSION_SHIFTS.length) return { number, shifts: 0, dismissed: true };
      return { number, shifts: SUSPENSION_SHIFTS[number - 1], dismissed: false };
    },

    /**
     * Serves it: the card is wiped and the count of suspensions is not.
     *
     * Those two halves are the whole rule. Strikes reset, because you have paid
     * for them and a player who comes back already on two has not been punished,
     * they have been retired. The tally does not, because the building
     * remembers that this is the second time — and standing does not either,
     * which is what makes coming back different from starting again.
     */
    serve() {
      const penalty = this.nextSuspension();
      served += 1;
      this.clear();
      return penalty;
    },

    clear() {
      entries = [];
      cooldowns.clear();
    },

    /** Everything, suspensions included. A new starter, not a clean week. */
    wipe() {
      this.clear();
      served = 0;
    },

    toJSON: () => ({ entries, served }),
    load(state) {
      entries = (state?.entries ?? []).map((entry) => ({
        ...entry,
        // A save written before the card had dates on it still has to read back
        // as a card. Filing day plus the window is what it would have said.
        clears: entry.clears ?? clearsOn(entry.day, decayShifts),
      }));
      served = state?.served ?? 0;
      cooldowns.clear();
    },
  };
}

/** The sentence a manager would put on the form. */
export function describe(reason) {
  switch (reason) {
    case REASONS.FOH:
      return 'Front of house in uniform';
    case REASONS.LATE:
      return 'Late for shift';
    case REASONS.NO_SHOW:
      return 'No show';
    case REASONS.DEADLINE:
      return 'Room not set by the doors';
    case REASONS.RESTRICTED:
      return 'In a restricted area';
    case REASONS.CIVILIAN:
      return 'Back of house out of uniform';
    case REASONS.REPORTED:
      return 'Reported by a colleague';
    case REASONS.THEFT:
      return 'Taking department property';
    default:
      return 'Conduct';
  }
}
